import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CloseIcon from '@mui/icons-material/Close';
import CurrencyLiraIcon from '@mui/icons-material/CurrencyLira';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import EuroIcon from '@mui/icons-material/Euro';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SaveIcon from '@mui/icons-material/Save';

import {
  BRAND,
  C,
  cancelButton,
  durumBilgisi,
  neutralButton,
  primaryButton,
  kodluSecenek,
  sayiyaCevir,
  trFiltre
} from './teklifTheme';
import { getKatalog, getTeklifDetay, teklifKaydet, toplamlariHesapla } from './teklifApi';
import { PARA_BIRIMLERI, getKurlar, tldenCevir, tutarFormatla } from './kurServisi';
import { GRUPLAR, getFiyatListeleri, getFiyatListesiKalemleri } from './fiyatListesiApi';
import FiyatListesiKalemSecimi from './FiyatListesiKalemSecimi';

// Teklif satırındaki grup adı → fiyat listesi grup kodu
const grupKodu = ad => GRUPLAR.find(g => g.ad === ad)?.kod || 'DIGER';
const yuvarla = n => Math.round(n * 100) / 100;

// Kayıtlı teklifte grup indirimi satırlarda tutuluyor; ana başlık değeri oradan okunur
const grupIndirimleriOku = kalemler =>
  Object.fromEntries(
    (kalemler || [])
      .filter(k => k.FiyatListesiKalemId && Number(k.GrupIndirimOrani) > 0)
      .map(k => [grupKodu(k.Grup), String(Number(k.GrupIndirimOrani))])
  );

const PARA_IKONLARI = {
  CurrencyLira: CurrencyLiraIcon,
  AttachMoney: AttachMoneyIcon,
  Euro: EuroIcon,
  CurrencyPound: CurrencyPoundIcon
};

const BOS_TEKLIF = {
  TeklifId: 0,
  TeklifNo: '',
  ProjeAdi: '',
  MusteriAdSoyad: '',
  MusteriTelefon: '',
  MusteriAdres: '',
  BayiId: null,
  ServisId: null,
  Durum: 1,
  GecerlilikGun: 15,
  ParaBirimi: 'TRY',
  DovizKuru: 1,
  SarfMalzemeOrani: 20,
  IndirimOrani: 0,
  KdvOrani: 20,
  Sartlar: '',
  FiyatListesiId: null,
  GrupIndirimleri: {},
  Kalemler: []
};

const styles = {
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    padding: '16px 24px',
    borderBottom: `1px solid ${C.borderSoft}`,
    backgroundColor: C.surface
  },
  titleIcon: {
    width: 40,
    height: 40,
    borderRadius: '12px',
    display: 'grid',
    placeItems: 'center',
    color: '#ffffff',
    flexShrink: 0,
    background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.hover} 100%)`
  },
  content: { padding: '24px', backgroundColor: C.headBg },
  section: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: '14px',
    padding: '20px',
    marginBottom: '16px'
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: C.inkMuted,
    marginBottom: '14px'
  },
  field: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: C.surface,
      '& fieldset': { borderColor: C.border },
      '&:hover fieldset': { borderColor: `${BRAND.main}99` },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${BRAND.main}29` },
      '&.Mui-focused fieldset': { borderColor: BRAND.main }
    }
  },
  headCell: {
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.border}`,
    padding: '10px 12px'
  },
  bodyCell: {
    padding: '8px 12px',
    fontSize: '0.875rem',
    color: C.inkBody,
    borderBottom: `1px solid ${C.borderSoft}`
  },
  ozetSatir: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    fontSize: '0.875rem',
    color: C.inkBody
  },
  ozetToplam: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    marginTop: '8px',
    borderRadius: '12px',
    backgroundColor: `${BRAND.main}1f`,
    border: `1px solid ${BRAND.main}4d`
  },
  actions: {
    padding: '16px 24px',
    gap: '8px',
    borderTop: `1px solid ${C.borderSoft}`,
    backgroundColor: C.surface
  }
};

function OfferDialog({
  open,
  mode,
  teklifId,
  kopyaKaynakId,
  bayiler,
  servisler,
  durumlar,
  yoneticiMi,
  kullaniciServisId,
  onClose,
  onSaved
}) {
  // Yönetici olmayan yalnız bağlı olduğu servis adına teklif hazırlar
  const servisSabit = !yoneticiMi;
  const sabitServisId = servisSabit && kullaniciServisId ? kullaniciServisId : null;
  const guncellemeMi = mode === 'update';
  const kopyaMi = !guncellemeMi && Number(kopyaKaynakId) > 0;

  const [form, setForm] = useState(BOS_TEKLIF);
  const [katalog, setKatalog] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');
  const [hatalar, setHatalar] = useState({});

  const [fiyatListeleri, setFiyatListeleri] = useState([]);
  const [listeKalemleri, setListeKalemleri] = useState([]);
  const [listeYukleniyor, setListeYukleniyor] = useState(false);
  // Güncellemede teklife kaydedilmiş fiyatlar; SP de bunları korur
  const [donmus, setDonmus] = useState({});
  const [bekleyenListe, setBekleyenListe] = useState(null);

  // TCMB kurlari: { kurlar, tarih, kaynak }
  const [kurBilgisi, setKurBilgisi] = useState({ kurlar: { TRY: 1 }, tarih: '', kaynak: '' });
  const [kurUyarisi, setKurUyarisi] = useState('');

  // Katalog bir kez cekiliyor.
  useEffect(() => {
    if (!open) return;
    getKatalog()
      .then(setKatalog)
      .catch(() => setHata('Kalem kataloğu yüklenemedi.'));
  }, [open]);

  // Kullanıcının rolüne açık aktif listeler; yeni teklifte tek liste varsa o seçilir
  useEffect(() => {
    if (!open) return;
    getFiyatListeleri(true)
      .then(({ listeler }) => {
        setFiyatListeleri(listeler);
        if (!guncellemeMi && !kopyaMi && listeler.length === 1) {
          setForm(f => (f.FiyatListesiId ? f : { ...f, FiyatListesiId: listeler[0].FiyatListesiId }));
        }
      })
      .catch(() => setHata('Fiyat listeleri yüklenemedi.'));
  }, [open, guncellemeMi, kopyaMi]);

  useEffect(() => {
    if (!open || !form.FiyatListesiId) {
      setListeKalemleri([]);
      return undefined;
    }
    let iptal = false;
    setListeYukleniyor(true);
    getFiyatListesiKalemleri(form.FiyatListesiId, true)
      .then(k => !iptal && setListeKalemleri(k))
      .catch(() => !iptal && setHata('Fiyat listesi kalemleri yüklenemedi.'))
      .finally(() => !iptal && setListeYukleniyor(false));
    return () => {
      iptal = true;
    };
  }, [open, form.FiyatListesiId]);

  // Gunluk kurlar. kurServisi onbellekli, tekrar tekrar istek atmaz.
  useEffect(() => {
    if (!open) return;
    getKurlar().then(bilgi => {
      setKurBilgisi(bilgi);
      if (bilgi.kaynak === 'varsayilan') {
        setKurUyarisi(
          'TCMB kur servisine ulaşılamadı; varsayılan kurlar kullanılıyor. Kuru elle kontrol ediniz.'
        );
      }
    });
  }, [open]);

  /** Secili para biriminin TCMB guncel kuru. Kullanici bunun USTUNE cikamaz. */
  const guncelKur = kurBilgisi.kurlar[form.ParaBirimi] || 1;
  const tlMi = form.ParaBirimi === 'TRY';

  // Dialog her acilista sifirlanir; guncellemede detay cekilir.
  useEffect(() => {
    if (!open) return;

    setHata('');
    setHatalar({});
    setDonmus({});
    setBekleyenListe(null);

    if (!guncellemeMi && !kopyaMi) {
      setForm({ ...BOS_TEKLIF, ServisId: sabitServisId, Kalemler: [] });
      return;
    }

    setYukleniyor(true);
    getTeklifDetay(guncellemeMi ? teklifId : kopyaKaynakId)
      .then(detay => {
        if (!detay) {
          setHata('Teklif detayı bulunamadı.');
          return;
        }

        if (guncellemeMi) {
          setForm({ ...detay, GrupIndirimleri: grupIndirimleriOku(detay.Kalemler) });
          setDonmus(
            Object.fromEntries(
              detay.Kalemler.filter(k => k.FiyatListesiKalemId && k.KaynakFiyat != null).map(k => [
                k.FiyatListesiKalemId,
                {
                  id: k.FiyatListesiKalemId,
                  grup: grupKodu(k.Grup),
                  tanim: k.Aciklama,
                  birim: k.Birim,
                  fiyat: Number(k.KaynakFiyat),
                  pb: k.KaynakParaBirimi
                }
              ])
            )
          );
          return;
        }

        // Kopya: kalemler ve oranlar korunur, kimlik ve durum sifirlanir; fiyatlar listeden güncel gelir.
        setForm({
          ...detay,
          TeklifId: 0,
          TeklifNo: '',
          Durum: 1,
          ...(servisSabit ? { ServisId: sabitServisId } : {}),
          ProjeAdi: `${detay.ProjeAdi || ''} (kopya)`.trim(),
          GrupIndirimleri: grupIndirimleriOku(detay.Kalemler),
          Kalemler: (detay.Kalemler || []).map((k, i) => ({
            ...k,
            SatirId: Date.now() + i
          }))
        });
      })
      .catch(() => setHata('Teklif detayı yüklenemedi. Lütfen tekrar deneyin.'))
      .finally(() => setYukleniyor(false));
  }, [open, guncellemeMi, kopyaMi, teklifId, kopyaKaynakId, servisSabit, sabitServisId]);

  const alanDegistir = (alan, deger) => {
    setForm(onceki => ({ ...onceki, [alan]: deger }));
    if (hatalar[alan]) setHatalar(onceki => ({ ...onceki, [alan]: '' }));
  };

  // -------------------------------------------------------- PARA BIRIMI / KUR

  /**
   * Para birimi degisince, TCMB'nin O GUN YAYINLADIGI kur dogrudan alana yazilir.
   * Kullanici kontrol edip elle duzeltebilir (asagi dogru; tavan guncel kur).
   *
   * Kurlar henuz gelmediyse alan 1'e dusurulmez; kullanici yanlis kurla teklif
   * hazirlamasin diye bos birakilir.
   */
  const paraBirimiDegistir = kod => {
    const yayinlanan = kurBilgisi.kurlar[kod];
    setKurUyarisi('');

    if (kod === 'TRY') {
      setForm(onceki => ({ ...onceki, ParaBirimi: kod, DovizKuru: 1 }));
      return;
    }

    if (!yayinlanan) {
      setKurUyarisi('Güncel kur henüz alınamadı. Kuru elle giriniz.');
      setForm(onceki => ({ ...onceki, ParaBirimi: kod, DovizKuru: '' }));
      return;
    }

    setForm(onceki => ({ ...onceki, ParaBirimi: kod, DovizKuru: yayinlanan }));
  };

  /**
   * Kur elle degistirilebilir ama TCMB guncel kurunun USTUNE cikamaz.
   * Asan deger girilirse guncel kura kirpilir ve kullaniciya sebebi soylenir.
   */
  const kurDegistir = deger => {
    const girilen = sayiyaCevir(deger);

    if (girilen > guncelKur) {
      setKurUyarisi(
        `Kur, TCMB güncel kurunun (${guncelKur.toFixed(4)}) üzerine çıkamaz. Değer güncel kura çekildi.`
      );
      setForm(onceki => ({ ...onceki, DovizKuru: guncelKur }));
      return;
    }

    setKurUyarisi('');
    setForm(onceki => ({ ...onceki, DovizKuru: deger }));
  };

  /** Ekranda gosterilecek tutar: katalog TL, gosterim secili para biriminde. */
  const goster = tutarTL =>
    tlMi
      ? tutarFormatla(tutarTL, 'TRY')
      : tutarFormatla(tldenCevir(tutarTL, form.DovizKuru), form.ParaBirimi);

  // ------------------------------------------------------------- KALEMLER
  const kalemHatasiTemizle = () => {
    if (hatalar.Kalemler) setHatalar(onceki => ({ ...onceki, Kalemler: '' }));
  };

  const miktarYaz = (id, deger) => {
    kalemHatasiTemizle();
    setForm(onceki => {
      const varMi = onceki.Kalemler.some(s => s.FiyatListesiKalemId === id);
      return {
        ...onceki,
        Kalemler: varMi
          ? onceki.Kalemler.map(s => (s.FiyatListesiKalemId === id ? { ...s, Miktar: deger } : s))
          : [...onceki.Kalemler, { SatirId: `fl-${id}`, FiyatListesiKalemId: id, Miktar: deger }]
      };
    });
  };

  const kalemSec = (satir, secili) => {
    if (secili) {
      miktarYaz(satir.id, satir.adet || 1);
      return;
    }
    setForm(onceki => ({
      ...onceki,
      Kalemler: onceki.Kalemler.filter(s => s.FiyatListesiKalemId !== satir.id)
    }));
  };

  const eskiMiktarYaz = (satirId, deger) =>
    setForm(onceki => ({
      ...onceki,
      Kalemler: onceki.Kalemler.map(s => (s.SatirId === satirId ? { ...s, Miktar: deger } : s))
    }));

  const eskiSil = satirId =>
    setForm(onceki => ({
      ...onceki,
      Kalemler: onceki.Kalemler.filter(s => s.SatirId !== satirId)
    }));

  // Liste değişince listeden seçilmiş kalemler bırakılır; eski katalog satırları kalır
  const listeUygula = id => {
    setForm(onceki => ({
      ...onceki,
      FiyatListesiId: id,
      Kalemler: onceki.Kalemler.filter(s => !s.FiyatListesiKalemId)
    }));
    setBekleyenListe(null);
  };

  const grupOrani = kod => Math.min(Math.max(sayiyaCevir(form.GrupIndirimleri?.[kod]), 0), 100);

  const grupIndirimYaz = (kod, deger) => {
    kalemHatasiTemizle();
    setForm(onceki => ({ ...onceki, GrupIndirimleri: { ...onceki.GrupIndirimleri, [kod]: deger } }));
  };

  const listeSec = id => {
    if (!id || id === form.FiyatListesiId) return;
    const secimVar = form.Kalemler.some(s => s.FiyatListesiKalemId && sayiyaCevir(s.Miktar) > 0);
    if (secimVar) setBekleyenListe(id);
    else listeUygula(id);
  };

  const katalogBul = kalemId => katalog.find(k => k.Id === kalemId);

  /** Ekrandaki liste satırları: güncel liste + teklifte kayıtlı fiyatlar + listede artık olmayan seçimler */
  const listeSatirlari = useMemo(() => {
    const harita = new Map();
    listeKalemleri.forEach(k =>
      harita.set(k.KalemId, {
        id: k.KalemId,
        grup: k.Grup,
        tanim: k.Tanim,
        birim: k.Birim,
        fiyat: Number(k.Fiyat),
        pb: k.ParaBirimi,
        adet: Number(k.Adet) || 1
      })
    );
    const seciliIdler = new Set(form.Kalemler.map(s => s.FiyatListesiKalemId).filter(Boolean));
    Object.values(donmus).forEach(d => {
      const guncel = harita.get(d.id);
      if (!guncel && !seciliIdler.has(d.id)) return;
      const degisti = guncel && (guncel.fiyat !== d.fiyat || guncel.pb !== d.pb);
      harita.set(d.id, {
        ...(guncel || { adet: 1 }),
        ...d,
        donmus: true,
        guncelFiyat: degisti ? `${guncel.fiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${guncel.pb}` : ''
      });
    });
    form.Kalemler.forEach(s => {
      if (s.FiyatListesiKalemId && !harita.has(s.FiyatListesiKalemId)) {
        harita.set(s.FiyatListesiKalemId, {
          id: s.FiyatListesiKalemId,
          grup: grupKodu(s.Grup),
          tanim: s.Aciklama || 'Listede olmayan kalem',
          birim: s.Birim,
          fiyat: null,
          pb: null,
          yok: true
        });
      }
    });
    // SP ile aynı kural: teklif dövizi → teklif kuru, diğerleri → TCMB
    const kurBul = pb =>
      pb === 'TRY' ? 1 : pb === form.ParaBirimi ? sayiyaCevir(form.DovizKuru) : Number(kurBilgisi.kurlar[pb]) || 0;
    return [...harita.values()].map(s => {
      const kur = s.yok ? 1 : kurBul(s.pb);
      return { ...s, kurYok: !s.yok && !kur, tl: s.yok ? 0 : yuvarla(s.fiyat * kur) };
    });
  }, [listeKalemleri, donmus, form.Kalemler, form.ParaBirimi, form.DovizKuru, kurBilgisi]);

  const satirHaritasi = useMemo(() => new Map(listeSatirlari.map(s => [s.id, s])), [listeSatirlari]);

  const kalemGruplari = useMemo(
    () =>
      GRUPLAR.map(g => ({ ...g, satirlar: listeSatirlari.filter(s => s.grup === g.kod) })).filter(
        g => g.satirlar.length
      ),
    [listeSatirlari]
  );

  const miktarlar = useMemo(
    () =>
      Object.fromEntries(
        form.Kalemler.filter(s => s.FiyatListesiKalemId).map(s => [s.FiyatListesiKalemId, s.Miktar])
      ),
    [form.Kalemler]
  );

  const eskiSatirlar = form.Kalemler
    .filter(s => s.KalemId)
    .map(s => {
      const k = katalogBul(s.KalemId);
      return {
        ...s,
        tanim: k?.Aciklama || s.Aciklama || '—',
        birim: k?.Birim || s.Birim,
        tl: Number(k ? k.BirimFiyat : s.BirimFiyat) || 0
      };
    });

  /** Kaydedilecek liste satırları: miktarı olan ve fiyatı bilinen */
  const listeSecimleri = form.Kalemler.filter(s => {
    const r = s.FiyatListesiKalemId && satirHaritasi.get(s.FiyatListesiKalemId);
    return r && !r.yok && sayiyaCevir(s.Miktar) > 0;
  });

  const toplamlar = useMemo(
    () =>
      toplamlariHesapla(
        {
          ...form,
          Kalemler: form.Kalemler.map(s =>
            s.FiyatListesiKalemId
              ? {
                  ...s,
                  BirimFiyat: satirHaritasi.get(s.FiyatListesiKalemId)?.tl || 0,
                  GrupIndirimOrani: grupOrani(satirHaritasi.get(s.FiyatListesiKalemId)?.grup)
                }
              : s
          )
        },
        katalog
      ),
    [form, katalog, satirHaritasi]
  );

  const listeSecenekleri = useMemo(() => {
    if (!form.FiyatListesiId || fiyatListeleri.some(l => l.FiyatListesiId === form.FiyatListesiId))
      return fiyatListeleri;
    // Teklifin listesi artık size açık değil ya da pasif; adıyla gösterilir
    return [
      { FiyatListesiId: form.FiyatListesiId, Ad: form.FiyatListesiAdi || `Liste #${form.FiyatListesiId}` },
      ...fiyatListeleri
    ];
  }, [fiyatListeleri, form.FiyatListesiId, form.FiyatListesiAdi]);

  // -------------------------------------------------------------- KAYDET
  const dogrula = () => {
    const yeni = {};
    if (!String(form.ProjeAdi || '').trim()) yeni.ProjeAdi = 'Proje adı zorunludur.';
    if (!form.BayiId) yeni.BayiId = 'Bayi seçiniz.';
    if (!form.ServisId)
      yeni.ServisId = servisSabit
        ? 'Apollo’da bağlı olduğunuz servis bulunamadı.'
        : 'Servis seçiniz.';
    if (!String(form.MusteriAdSoyad || '').trim())
      yeni.MusteriAdSoyad = 'Müşteri adı zorunludur.';
    if (!listeSecimleri.length && !eskiSatirlar.length)
      yeni.Kalemler = form.FiyatListesiId
        ? 'En az bir kaleme miktar giriniz.'
        : 'Fiyat listesi seçip en az bir kaleme miktar giriniz.';
    else if (GRUPLAR.some(g => { const o = sayiyaCevir(form.GrupIndirimleri?.[g.kod]); return o < 0 || o > 100; }))
      yeni.Kalemler = 'Grup indirimi 0 ile 100 arasında olmalı.';
    else if (listeSecimleri.some(s => satirHaritasi.get(s.FiyatListesiKalemId)?.kurYok))
      yeni.Kalemler = 'Kuru alınamayan dövizli kalem var. Teklif para birimini o döviz seçip kuru elle giriniz.';

    setHatalar(yeni);
    return Object.keys(yeni).length === 0;
  };

  const kaydet = () => {
    if (!dogrula()) {
      setHata('Lütfen işaretli alanları kontrol ediniz.');
      return;
    }

    setHata('');
    setKaydediliyor(true);

    const secilenBayi = bayiler.find(b => b.Id === form.BayiId);
    const secilenServis = servisler.find(s => s.Id === form.ServisId);

    teklifKaydet({
      ...form,
      Kalemler: [...form.Kalemler.filter(s => s.KalemId), ...listeSecimleri].map(s => ({
        ...s,
        Miktar: sayiyaCevir(s.Miktar)
      })),
      GrupIndirimleri: Object.fromEntries(GRUPLAR.map(g => [g.kod, grupOrani(g.kod)])),
      Bayi: secilenBayi ? secilenBayi.Dsc : '',
      Servis: secilenServis ? secilenServis.Dsc : ''
    })
      .then(sonuc => {
        setKaydediliyor(false);
        if (sonuc?.Basarili === false) {
          setHata(sonuc.Mesaj || 'Teklif kaydedilemedi.');
          return;
        }
        onSaved();
      })
      .catch(() => {
        setKaydediliyor(false);
        setHata('Teklif kaydedilemedi. Lütfen tekrar deneyin.');
      });
  };

  const kalemHatasi = hatalar.Kalemler;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleIcon}>
          <ReceiptLongIcon />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>
            {guncellemeMi ? 'Teklif Güncelleme' : 'Yeni Teklif Oluşturma'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: C.inkMuted }}>
              {guncellemeMi && form.TeklifNo
                ? form.TeklifNo
                : 'Proje, müşteri ve kalem bilgilerini giriniz'}
            </Typography>
            {kopyaMi && (
              <Chip
                size="small"
                label="Kopyalandı"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  backgroundColor: `${BRAND.main}1f`,
                  color: '#9a5b00'
                }}
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        {hata && (
          <Alert severity="warning" onClose={() => setHata('')} sx={{ mb: 2, borderRadius: '10px' }}>
            {hata}
          </Alert>
        )}

        {yukleniyor ? (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320, gap: 2 }}>
            <CircularProgress sx={{ color: BRAND.main }} />
            <Typography variant="body2" sx={{ color: C.inkMuted }}>
              Teklif bilgileri yükleniyor...
            </Typography>
          </Box>
        ) : (
          <>
            {/* ---------------------------------------------- TEKLIF BILGILERI */}
            <Box sx={styles.section}>
              <Typography sx={styles.sectionTitle}>Teklif Bilgileri</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Proje Adı"
                    placeholder="Örn: Kuzey Rezidans"
                    value={form.ProjeAdi}
                    onChange={e => alanDegistir('ProjeAdi', e.target.value)}
                    error={Boolean(hatalar.ProjeAdi)}
                    helperText={hatalar.ProjeAdi}
                    sx={styles.field}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    filterOptions={trFiltre}
                    size="small"
                    options={bayiler}
                    renderOption={kodluSecenek}
                    getOptionLabel={o => o.Dsc || ''}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    value={bayiler.find(b => b.Id === form.BayiId) || null}
                    onChange={(e, v) => alanDegistir('BayiId', v ? v.Id : null)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Bayi"
                        error={Boolean(hatalar.BayiId)}
                        helperText={hatalar.BayiId}
                        sx={styles.field}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    filterOptions={trFiltre}
                    size="small"
                    options={servisler}
                    renderOption={kodluSecenek}
                    getOptionLabel={o => o.Dsc || ''}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    value={servisler.find(s => s.Id === form.ServisId) || null}
                    onChange={(e, v) => alanDegistir('ServisId', v ? v.Id : null)}
                    disabled={servisSabit}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Servis"
                        error={Boolean(hatalar.ServisId)}
                        helperText={hatalar.ServisId || (servisSabit ? 'Bağlı olduğunuz servis' : '')}
                        sx={styles.field}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Durum"
                    value={form.Durum || 1}
                    onChange={e => alanDegistir('Durum', Number(e.target.value))}
                    sx={styles.field}
                  >
                    {(durumlar.length ? durumlar : [{ Id: 1, Dsc: 'Taslak' }]).map(d => (
                      <MenuItem key={d.Id} value={d.Id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: durumBilgisi(d.Id).fg
                            }}
                          />
                          {d.Dsc}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Geçerlilik (gün)"
                    value={form.GecerlilikGun}
                    onChange={e => alanDegistir('GecerlilikGun', e.target.value)}
                    sx={styles.field}
                  />
                </Grid>

                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Para Birimi"
                    value={form.ParaBirimi}
                    onChange={e => paraBirimiDegistir(e.target.value)}
                    sx={styles.field}
                  >
                    {PARA_BIRIMLERI.map(pb => {
                      const Ikon = PARA_IKONLARI[pb.ikon];
                      return (
                        <MenuItem key={pb.kod} value={pb.kod}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Ikon sx={{ fontSize: '1.1rem', color: BRAND.main }} />
                            {pb.kod} — {pb.ad}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={tlMi ? 'Kur (TL için geçerli değil)' : `Kur — 1 ${form.ParaBirimi} = ? TL`}
                    value={tlMi ? 1 : form.DovizKuru}
                    disabled={tlMi}
                    onChange={e => kurDegistir(e.target.value)}
                    inputProps={{ min: 0, max: guncelKur, step: '0.0001' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="TCMB güncel kuruna çek" arrow>
                            <span>
                              <Button
                                size="small"
                                disabled={tlMi}
                                onClick={() => alanDegistir('DovizKuru', guncelKur)}
                                sx={{
                                  minWidth: 0,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  color: BRAND.main
                                }}
                              >
                                Güncel
                              </Button>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    helperText={
                      tlMi
                        ? 'Teklif TL olarak hazırlanıyor, kur uygulanmaz.'
                        : `TCMB ${kurBilgisi.tarih || 'bugün'} kuru yazıldı: ${guncelKur.toFixed(
                            4
                          )} TL · kontrol edip düşürebilirsiniz, üzerine çıkılamaz`
                    }
                    sx={styles.field}
                  />
                </Grid>
              </Grid>

              {kurUyarisi && (
                <Alert
                  severity="info"
                  onClose={() => setKurUyarisi('')}
                  sx={{ mt: 2, borderRadius: '10px' }}
                >
                  {kurUyarisi}
                </Alert>
              )}
            </Box>

            {/* ---------------------------------------------- MUSTERI BILGILERI */}
            <Box sx={styles.section}>
              <Typography sx={styles.sectionTitle}>Müşteri Bilgileri</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ad Soyad"
                    value={form.MusteriAdSoyad}
                    onChange={e => alanDegistir('MusteriAdSoyad', e.target.value)}
                    error={Boolean(hatalar.MusteriAdSoyad)}
                    helperText={hatalar.MusteriAdSoyad}
                    sx={styles.field}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Telefon"
                    placeholder="05XX XXX XX XX"
                    value={form.MusteriTelefon}
                    onChange={e => alanDegistir('MusteriTelefon', e.target.value)}
                    sx={styles.field}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Adres"
                    value={form.MusteriAdres}
                    onChange={e => alanDegistir('MusteriAdres', e.target.value)}
                    sx={styles.field}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* ---------------------------------------------------- KALEMLER */}
            <Box sx={styles.section}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 1.5
                }}
              >
                <Typography sx={{ ...styles.sectionTitle, marginBottom: 0 }}>
                  Teklif Kalemleri
                </Typography>
                <Autocomplete
                  filterOptions={trFiltre}
                  size="small"
                  disableClearable
                  options={listeSecenekleri}
                  getOptionLabel={o => o.Ad || ''}
                  isOptionEqualToValue={(o, v) => o.FiyatListesiId === v.FiyatListesiId}
                  value={listeSecenekleri.find(l => l.FiyatListesiId === form.FiyatListesiId) || null}
                  onChange={(e, v) => listeSec(v?.FiyatListesiId)}
                  noOptionsText="Size açılmış fiyat listesi yok"
                  sx={{ width: { xs: '100%', sm: 360 } }}
                  renderOption={({ key: _k, ...p }, o) => (
                    <li {...p} key={o.FiyatListesiId}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                        <span>{o.Ad}</span>
                        {o.KalemSayisi != null && (
                          <Typography component="span" sx={{ color: C.inkMuted, fontSize: '0.75rem' }}>
                            {o.KalemSayisi} kalem
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Fiyat Listesi"
                      placeholder="Fiyat listesi seçin"
                      error={Boolean(kalemHatasi) && !form.FiyatListesiId}
                      sx={styles.field}
                    />
                  )}
                />
              </Box>

              {kalemHatasi && (
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '10px' }}>
                  {kalemHatasi}
                </Alert>
              )}

              <FiyatListesiKalemSecimi
                gruplar={kalemGruplari}
                eskiSatirlar={eskiSatirlar}
                miktarlar={miktarlar}
                paraBirimi={form.ParaBirimi}
                goster={goster}
                listeSecili={Boolean(form.FiyatListesiId)}
                yukleniyor={listeYukleniyor}
                onMiktar={miktarYaz}
                onSec={kalemSec}
                onEskiMiktar={eskiMiktarYaz}
                onEskiSil={eskiSil}
                grupIndirimleri={form.GrupIndirimleri || {}}
                onGrupIndirim={grupIndirimYaz}
              />
            </Box>

            {/* ---------------------------------------------------- TOPLAMLAR */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ ...styles.section, marginBottom: 0, height: '100%' }}>
                  <Typography sx={styles.sectionTitle}>Teklif Şartları</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    size="small"
                    placeholder="Teklife eklenecek şartlar ve bilgilendirme notları"
                    value={form.Sartlar}
                    onChange={e => alanDegistir('Sartlar', e.target.value)}
                    sx={styles.field}
                  />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ ...styles.section, marginBottom: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5
                    }}
                  >
                    <Typography sx={{ ...styles.sectionTitle, marginBottom: 0 }}>
                      Toplamlar
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        tlMi
                          ? 'TRY'
                          : `${form.ParaBirimi} · 1 = ${sayiyaCevir(form.DovizKuru).toFixed(4)} TL`
                      }
                      sx={{
                        height: 24,
                        borderRadius: '7px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: `${BRAND.main}1f`,
                        color: '#9a5b00'
                      }}
                    />
                  </Box>

                  {toplamlar.grupIndirimi > 0.004 && (
                    <>
                      <Box sx={styles.ozetSatir}>
                        <span>Kalem Toplamı</span>
                        <strong>{goster(toplamlar.brutToplam)}</strong>
                      </Box>
                      <Box sx={styles.ozetSatir}>
                        <span>Grup İndirimleri</span>
                        <strong style={{ color: BRAND.danger }}>-{goster(toplamlar.grupIndirimi)}</strong>
                      </Box>
                      <Divider />
                    </>
                  )}

                  <Box sx={styles.ozetSatir}>
                    <span>Ara Toplam</span>
                    <strong>{goster(toplamlar.araToplam)}</strong>
                  </Box>
                  <Divider />

                  <Box sx={styles.ozetSatir}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Sarf Malzeme ve Genel Giderler</span>
                      <TextField
                        size="small"
                        type="number"
                        value={form.SarfMalzemeOrani}
                        onChange={e => alanDegistir('SarfMalzemeOrani', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ ...styles.field, width: 96 }}
                      />
                    </Box>
                    <strong>{goster(toplamlar.sarf)}</strong>
                  </Box>
                  <Divider />

                  <Box sx={styles.ozetSatir}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Özel İndirim</span>
                      <TextField
                        size="small"
                        type="number"
                        value={form.IndirimOrani}
                        onChange={e => alanDegistir('IndirimOrani', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ ...styles.field, width: 96 }}
                      />
                    </Box>
                    <strong style={{ color: BRAND.danger }}>
                      -{goster(toplamlar.indirim)}
                    </strong>
                  </Box>
                  <Divider />

                  <Box sx={styles.ozetSatir}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>KDV</span>
                      <TextField
                        size="small"
                        type="number"
                        value={form.KdvOrani}
                        onChange={e => alanDegistir('KdvOrani', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ ...styles.field, width: 96 }}
                      />
                    </Box>
                    <strong>{goster(toplamlar.kdv)}</strong>
                  </Box>

                  <Box sx={styles.ozetToplam}>
                    <Typography sx={{ fontWeight: 700, color: C.ink }}>GENEL TOPLAM</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#9a5b00' }}>
                      {goster(toplamlar.genelToplam)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={styles.actions}>
        {/* Genel toplam her zaman gorunur; asagi kaydirmaya gerek kalmiyor. */}
        <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: C.inkMuted }}>
            GENEL TOPLAM
          </Typography>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#9a5b00' }}>
            {goster(toplamlar.genelToplam)}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onClose} sx={cancelButton} disabled={kaydediliyor}>
          Vazgeç
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={neutralButton}
          endIcon={<CloseIcon />}
          disabled={kaydediliyor}
        >
          Kapat
        </Button>
        <Button
          variant="contained"
          onClick={kaydet}
          sx={primaryButton}
          disabled={kaydediliyor || yukleniyor}
          startIcon={
            kaydediliyor ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />
          }
        >
          {kaydediliyor ? 'Kaydediliyor' : 'Kaydet'}
        </Button>
      </DialogActions>

      <Dialog
        open={bekleyenListe !== null}
        onClose={() => setBekleyenListe(null)}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: C.ink }}>Fiyat listesi değişsin mi?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: C.inkBody }}>
            Bu listeden miktar girdiğiniz kalemler tekliften çıkarılır.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px', gap: 1 }}>
          <Button sx={cancelButton} onClick={() => setBekleyenListe(null)}>
            Vazgeç
          </Button>
          <Button sx={primaryButton} onClick={() => listeUygula(bekleyenListe)}>
            Listeyi değiştir
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

OfferDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'update']).isRequired,
  teklifId: PropTypes.number,
  kopyaKaynakId: PropTypes.number,
  bayiler: PropTypes.array,
  servisler: PropTypes.array,
  durumlar: PropTypes.array,
  yoneticiMi: PropTypes.bool,
  kullaniciServisId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};

OfferDialog.defaultProps = {
  teklifId: 0,
  kopyaKaynakId: 0,
  bayiler: [],
  servisler: [],
  durumlar: [],
  yoneticiMi: true,
  kullaniciServisId: 0
};

export default OfferDialog;
